import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { ChevronDown, X } from 'lucide-react';
import { useOnClickOutside } from '@/components/ui/hooks/use-on-click-outside';
import { Label } from '@/components/ui/label';
export type DateRangeValue = {
  preset: 'today' | 'yesterday' | 'last7' | 'last30' | 'custom' | null;
  from: string | null;
  to: string | null;
  displayLabel: string;
};

type DateRangeFilterProps = {
  label: string;
  value: DateRangeValue | null;
  onChange: (value: DateRangeValue | null) => void;
  disabled?: boolean;
  inline?: boolean;
};

const presets = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7', label: 'Last 7 Days' },
  { key: 'last30', label: 'Last 30 Days' }
];

const getPresetDates = (preset: string): { from: string; to: string } => {
  const today = dayjs().format('YYYY-MM-DD');
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
      return { from: yesterday, to: yesterday };
    }
    case 'last7': {
      const from = dayjs().subtract(6, 'day').format('YYYY-MM-DD');
      return { from, to: today };
    }
    case 'last30': {
      const from = dayjs().subtract(29, 'day').format('YYYY-MM-DD');
      return { from, to: today };
    }
    default:
      return { from: today, to: today };
  }
};

const presetLabelMap: Record<string, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7: 'Last 7 days',
  last30: 'Last 30 days'
};
const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  inline = false
}) => {
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const emitCustomRange = (from: string, to: string) => {
    if (from && to) {
      onChange({ preset: 'custom', from, to, displayLabel: `${dayjs(from).format('MMM D, YYYY')} – ${dayjs(to).format('MMM D, YYYY')}` });
    }
  };

  useOnClickOutside(containerRef, () => setOpen(false));

  useEffect(() => {
    if (value?.preset === 'custom') {
      setCustomFrom(value.from || '');
      setCustomTo(value.to || '');
    } else if (value === null) {
      setCustomFrom('');
      setCustomTo('');
    }
  }, [value]);

  const handlePresetClick = (preset: string) => {
    const { from, to } = getPresetDates(preset);
    onChange({
      preset: preset as DateRangeValue['preset'],
      from,
      to,
      displayLabel: presetLabelMap[preset]
    });
    setOpen(false);
  };

  const handleCustomApply = () => {
    if (!customFrom || !customTo) return;
    const fromFormatted = dayjs(customFrom).format('MMM D, YYYY');
    const toFormatted = dayjs(customTo).format('MMM D, YYYY');
    onChange({
      preset: 'custom',
      from: customFrom,
      to: customTo,
      displayLabel: `${fromFormatted} – ${toFormatted}`
    });
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setCustomFrom('');
    setCustomTo('');
    setOpen(false);
  };

  const isActive = value !== null && value.from !== null;

  if (inline) {
    return (
      <div className="static block">
        <Label className="block mb-3">{label}</Label>
        <ul className="list-none m-0 p-0">
          {presets.map((p) => (
            <li key={p.key} className="p-0 flex items-center">
              <button
                type="button"
                className={`block flex-1 py-2 px-0 text-sm font-normal text-foreground cursor-pointer bg-transparent border-none text-left hover:text-primary${
                  value?.preset === p.key
                    ? ' text-primary font-medium'
                    : ''
                }`}
                onClick={() => handlePresetClick(p.key)}
              >
                {p.label}
              </button>
              {value?.preset === p.key && (
                <button
                  type="button"
                  className="bg-transparent border-none text-muted-foreground text-base leading-none py-1 pl-1 pr-0 cursor-pointer shrink-0 hover:text-primary"
                  onClick={handleClear}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
        <div className="flex flex-col gap-2.5 mt-1">
          <div className="border-t border-input mb-0.5" />
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-foreground">
              Custom range
            </span>
            {value?.preset === 'custom' && (
              <button
                type="button"
                className="bg-transparent border-none outline-none text-muted-foreground text-base leading-none p-0 pl-1 cursor-pointer hover:text-primary"
                onClick={handleClear}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={customFrom}
              max={customTo || undefined}
              onChange={(e) => { setCustomFrom(e.target.value); emitCustomRange(e.target.value, customTo); }}
              className="flex-1 h-9 border border-input rounded-md py-1 px-2 text-sm bg-transparent text-foreground min-w-0 focus:outline-none focus:border-primary"
            />
            <span className="text-muted-foreground text-sm shrink-0">
              –
            </span>
            <input
              type="date"
              value={customTo}
              min={customFrom || undefined}
              onChange={(e) => { setCustomTo(e.target.value); emitCustomRange(customFrom, e.target.value); }}
              className="flex-1 h-9 border border-input rounded-md py-1 px-2 text-sm bg-transparent text-foreground min-w-0 focus:outline-none focus:border-primary"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative block w-full" ref={containerRef}>
      <button
        type="button"
        className={`w-full pl-3 pr-2 text-muted-foreground text-sm font-normal border border-input bg-background hover:bg-muted h-10 rounded-md whitespace-nowrap flex items-center gap-1.5${
          isActive ? ' text-foreground' : ''
        }`}
        disabled={disabled}
        onClick={() => !disabled && setOpen(!open)}
      >
        <span className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap">
          {isActive ? value!.displayLabel : label}
        </span>
        {isActive ? (
          <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
        ) : (
          <span className="ml-1 flex items-center">
            <ChevronDown className="h-4 w-4" />
          </span>
        )}
      </button>

      {open && (
        <div className="absolute top-[calc(100%+6px)] left-0 z-[9999] bg-popover border border-border rounded-lg shadow-xl min-w-[260px] py-2">
          <ul className="list-none m-0 p-0">
            {presets.map((p) => (
              <li key={p.key} className="p-0 flex items-center">
                <button
                  type="button"
                  className={`block flex-1 py-2.5 px-4 text-sm font-normal text-foreground cursor-pointer bg-transparent border-none text-left hover:bg-muted${
                    value?.preset === p.key
                      ? ' text-primary font-medium'
                      : ''
                  }`}
                  onClick={() => handlePresetClick(p.key)}
                >
                  {p.label}
                </button>
                {value?.preset === p.key && (
                  <button
                    type="button"
                    className="bg-transparent border-none text-muted-foreground text-base leading-none py-1 pr-3 pl-1 cursor-pointer shrink-0 hover:text-primary"
                    onClick={handleClear}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </li>
            ))}
            <li className="cursor-default border-t border-input pt-3 px-4 pb-2">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-foreground">
                    Custom range
                  </span>
                  {value?.preset === 'custom' && (
                    <button
                      type="button"
                      className="bg-transparent border-none outline-none text-muted-foreground text-base leading-none p-0 pl-1 cursor-pointer hover:text-primary"
                      onClick={handleClear}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <input
                    type="date"
                    value={customFrom}
                    max={customTo || undefined}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="flex-1 h-9 border border-input rounded-md py-1 px-2 text-sm bg-transparent text-foreground min-w-0 focus:outline-none focus:border-primary"
                  />
                  <span className="text-muted-foreground text-sm shrink-0">
                    –
                  </span>
                  <input
                    type="date"
                    value={customTo}
                    min={customFrom || undefined}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="flex-1 h-9 border border-input rounded-md py-1 px-2 text-sm bg-transparent text-foreground min-w-0 focus:outline-none focus:border-primary"
                  />
                </div>
                <button
                  type="button"
                  className="self-end w-auto py-1.5 px-4 text-sm bg-primary text-primary-foreground border-none rounded-md cursor-pointer hover:bg-primary-80 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!customFrom || !customTo}
                  onClick={handleCustomApply}
                >
                  Apply
                </button>
              </div>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
